import React, { useState, useRef, useEffect } from 'react';
import './SystemAdmin.css';
import { seatsUsed, orgInTrial, orgTrialDaysLeft, orgEffectiveSeats, orgSubscribed } from './tenancy';
import Icon from './Icon';
import { isDevMode } from './dev';

const fmtDate = (v) => { const d = new Date(v); return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); };

function SystemAdmin({ orgs, accounts, mlsCatalog = [], onCreateOrg, onUpdateOrg, onSetOrgDisabled, onSetOrgExpiry, onDeleteOrg, onBuildBackup, onRestore, onResetAdminPassword, onManage, mlsId = null }) {
  const restoreRef = useRef(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', trialSeats: 50, trialDays: 30, seats: 0, mlsId: '', adminName: '', adminEmail: '' });
  const mlsName = (id) => (mlsCatalog.find(m => m.id === id) || {}).name || '—';
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', seats: 0 });
  const [manageId, setManageId] = useState(null);     // org whose admins are expanded
  const [confirmDelete, setConfirmDelete] = useState(null);   // org id in the delete flow
  const [delBackedUp, setDelBackedUp] = useState(false);      // gate 1: backup downloaded
  const [delName, setDelName] = useState('');                 // gate 2: typed org name
  const [delAck, setDelAck] = useState(false);                // gate 3: acknowledgement

  // "Used" = active agents + extra admins (the first admin is free). Shared with App.
  const activeUsed = (id) => seatsUsed(accounts, id);
  const adminsOf  = (id) => accounts.filter(a => a.orgId === id && a.admin && !a.system);
  // Both kinds of org now live on their MLS page: licensed (regular) orgs and the one
  // System Org that holds this MLS's direct iBetterAgent signups — both filtered to mlsId.
  const regularOrgs = orgs.filter(o => !o.systemOrg && (!mlsId || o.mlsId === mlsId));
  const systemOrgs  = orgs.filter(o => o.systemOrg && (!mlsId || o.mlsId === mlsId));
  const membersOf   = (id) => accounts.filter(a => a.orgId === id && !a.system);

  async function create() {
    const adminEmail = form.adminEmail.trim();
    const err = await onCreateOrg({ ...form, mlsId: mlsId || form.mlsId });
    if (err) { setError(err); return; }
    setNotice(`Organization "${form.name.trim()}" created — a setup link was emailed to ${adminEmail}, where the admin sets their password.`);
    setForm({ name: '', trialSeats: 50, trialDays: 30, seats: 0, mlsId: '', adminName: '', adminEmail: '' });
    setShowCreate(false); setError('');
  }
  function startEdit(o) { setEditId(o.id); setEditDraft({ name: o.name, seats: o.seats || 0, mlsId: o.mlsId || '', trialDays: o.trialDays || 0, trialSeats: o.trialSeats || 0 }); }
  async function saveEdit() {
    const err = await onUpdateOrg(editId, editDraft);
    if (err) { setNotice('⚠ ' + err); return; }   // keep the row in edit mode so they can fix it
    setEditId(null); setNotice('Organization updated.');
  }
  async function doReset(adminId, username, email) {
    if (!email) { setNotice(`⚠ "${username}" has no email — add one before sending a reset link.`); return; }
    setNotice('Sending reset link…');
    const r = await onResetAdminPassword(adminId);
    if (r?.error) { setNotice(`⚠ ${r.error}`); return; }
    setNotice(r?.sent ? `Reset link sent to ${email}.` : `Reset link generated for "${username}".`);
  }
  // ── Org deletion: deliberately hard. Requires a downloaded backup, typing the
  //    org's exact name, and an explicit acknowledgement before it's allowed. ──
  function startDelete(o) { setConfirmDelete(o.id); setDelBackedUp(false); setDelName(''); setDelAck(false); }
  function cancelDelete() { setConfirmDelete(null); setDelBackedUp(false); setDelName(''); setDelAck(false); }
  function downloadBackup(o) {
    const backup = onBuildBackup ? onBuildBackup(o.id) : null;
    if (!backup) { setNotice('⚠ Could not build a backup for this organization.'); return; }
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = (o.name || 'org').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.href = url;
    a.download = `ibetteragent-backup-${slug}-${backup.exportedAt.slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDelBackedUp(true);
    setNotice(`Backup downloaded for "${o.name}". You may now complete deletion.`);
  }
  function doDelete(o) {
    onDeleteOrg(o.id);
    cancelDelete();
    setNotice(`Organization "${o.name}" and all its data were deleted.`);
  }
  function handleRestoreFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';   // reset so the same file can be picked again
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let backup;
      try { backup = JSON.parse(reader.result); } catch { setNotice('⚠ That file isn’t valid JSON.'); return; }
      const err = onRestore ? onRestore(backup) : 'Restore is unavailable.';
      if (err) { setNotice(`⚠ ${err}`); return; }
      setNotice(`Organization “${backup.org?.name}” restored from backup.`);
    };
    reader.onerror = () => setNotice('⚠ Could not read that file.');
    reader.readAsText(file);
  }

  return (
    <div className="sa-container">
      <div className="sa-header sa-sysorg-header sa-orgs-header">
        <div>
          <h2 className="sa-sysorg-title sa-orgs-title"><Icon name="organizations" size={18} style={{verticalAlign:'-3px'}} /> Organizations</h2>
          <p>Organizations licensed under this MLS, their seats, and their admins.</p>
        </div>
        <div className="sa-header-actions">
          <button className="sa-add-btn" onClick={() => { setShowCreate(s => !s); setError(''); }}>+ <span className="sa-label-full">Create Organization</span><span className="sa-label-short">Create</span></button>
          <button className="sa-restore-btn" onClick={() => restoreRef.current && restoreRef.current.click()}><span className="sa-label-full"><Icon name="restore" size={16} /> Restore from backup</span><span className="sa-label-short">Restore</span></button>
          <input ref={restoreRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={handleRestoreFile} />
        </div>
      </div>

      {notice && <div className="sa-notice">{notice} <button className="sa-notice-x" onClick={() => setNotice('')}>✕</button></div>}

      {showCreate && (
        <div className="sa-panel">
          <div className="sa-panel-title">New Organization</div>
          {error && <div className="sa-error">⚠ {error}</div>}
          {!mlsId && mlsCatalog.length === 0 && <div className="sa-mls-hint">⚠ No MLS provisioned yet. Add one in the “Supported MLS” tab first — every organization must belong to an MLS.</div>}
          <div className="sa-grid">
            <div className="sa-field">
              <label>Organization name</label>
              <input value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setError(''); }} placeholder="e.g. Windermere" />
            </div>
            <div className="sa-field">
              <label>Trial seats</label>
              <input type="number" min="0" value={form.trialSeats} onChange={e => setForm(f => ({ ...f, trialSeats: e.target.value }))}
                title="Seats the org can use free during the trial period." />
            </div>
            <div className="sa-field">
              <label>Trial period (days)</label>
              <input type="number" min="0" value={form.trialDays} onChange={e => setForm(f => ({ ...f, trialDays: e.target.value }))}
                title="Length of the free trial from today. 0 = no trial (start paid)." />
            </div>
            <div className="sa-field">
              <label>Purchased seats</label>
              <input type="number" min="0" value={form.seats} onChange={e => setForm(f => ({ ...f, seats: e.target.value }))}
                title="Paid seats that take effect when the trial ends. 0 = not purchased yet (org locks when the trial ends)." />
            </div>
            {!mlsId && (
              <div className="sa-field">
                <label>MLS</label>
                <select value={form.mlsId} onChange={e => { setForm(f => ({ ...f, mlsId: e.target.value })); setError(''); }}>
                  <option value="">— Select MLS —</option>
                  {mlsCatalog.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div className="sa-field">
              <label>Admin email</label>
              <input type="email" value={form.adminEmail} onChange={e => { setForm(f => ({ ...f, adminEmail: e.target.value })); setError(''); }} placeholder="admin@example.com" />
            </div>
            <div className="sa-field">
              <label>Admin / manager name</label>
              <input value={form.adminName} onChange={e => { setForm(f => ({ ...f, adminName: e.target.value })); setError(''); }} placeholder="Admin's full name" />
            </div>
          </div>
          <div className="sa-panel-actions">
            <button className="rt-save-btn" onClick={create}>Create</button>
            <button className="contacts-import-cancel-btn" onClick={() => { setShowCreate(false); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {confirmDelete && (() => {
        const o = orgs.find(x => x.id === confirmDelete);
        if (!o) return null;
        const members = accounts.filter(a => a.orgId === o.id && !a.system);
        const adminCount = members.filter(a => a.admin).length;
        const agentCount = members.length - adminCount;
        const nameOk = delName.trim() === o.name;
        const canDelete = delBackedUp && nameOk && delAck;
        return (
          <div className="sa-delete-panel">
            <div className="sa-delete-title"><Icon name="delete" size={18} /> Delete “{o.name}” — danger zone</div>
            <p className="sa-delete-warn">
              This permanently removes the organization and <strong>everything in it</strong>:{' '}
              {adminCount} admin{adminCount === 1 ? '' : 's'}, {agentCount} agent{agentCount === 1 ? '' : 's'}, and all of their
              contacts, properties, reminders, and assigned reminders. <strong>This cannot be undone.</strong>
            </p>
            <ol className="sa-delete-steps">
              <li className={delBackedUp ? 'done' : ''}>
                <div className="sa-delete-step-label">{delBackedUp ? '✓' : '1.'} Back up everything first</div>
                <button className="sa-backup-btn" onClick={() => downloadBackup(o)}>
                  {delBackedUp ? '✓ Backup downloaded — download again' : <><Icon name="export" size={16} /> Download backup (.json)</>}
                </button>
              </li>
              <li className={nameOk ? 'done' : ''}>
                <div className="sa-delete-step-label">{nameOk ? '✓' : '2.'} Type the organization name <code>{o.name}</code> to confirm</div>
                <input className="sa-delete-input" placeholder={o.name} value={delName} disabled={!delBackedUp}
                  onChange={e => setDelName(e.target.value)} />
              </li>
              <li className={delAck ? 'done' : ''}>
                <label className="sa-delete-ack">
                  <input type="checkbox" checked={delAck} disabled={!delBackedUp} onChange={e => setDelAck(e.target.checked)} />
                  <span>{delAck ? '✓' : '3.'} I understand this permanently deletes all accounts and data for this organization.</span>
                </label>
              </li>
            </ol>
            <div className="sa-delete-actions">
              <button className="sa-delete-final" disabled={!canDelete} onClick={() => doDelete(o)}>
                Permanently delete “{o.name}”
              </button>
              <button className="contacts-import-cancel-btn" onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        );
      })()}

      {regularOrgs.length === 0 ? (
        <div className="sa-empty"><span><Icon name="organizations" size={18} /></span><p>No organizations yet. Click “Create Organization” to provision the first one for this MLS.</p></div>
      ) : (
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr><th>Organization</th>{!mlsId && <th>MLS</th>}<th>Used</th><th>Trial</th><th>Seats</th><th>Subscription</th><th>Status</th><th>Admins</th><th className="sa-th-actions">Actions</th></tr>
            </thead>
            <tbody>
              {regularOrgs.map(o => {
                const used = activeUsed(o.id);
                const eff = orgEffectiveSeats(o);
                const inTrial = orgInTrial(o);
                const daysLeft = inTrial ? orgTrialDaysLeft(o) : null;
                const subscribed = orgSubscribed(o);              // paid subscription still current
                const subLapsed = !!o.expiresAt && !subscribed;   // subscription date set but past
                // Blocked/expired: subscription lapsed, or (no subscription set) trial over with no seats.
                const expired = subLapsed || (!o.expiresAt && !!o.trialDays && !inTrial && (Number(o.seats) || 0) <= 0);
                const admins = adminsOf(o.id);
                const editing = editId === o.id;
                return (
                  <React.Fragment key={o.id}>
                    <tr>
                      <td className="sa-org-name" data-label="Organization">
                        {editing
                          ? <input className="sa-inline" value={editDraft.name} onChange={e => setEditDraft(d => ({ ...d, name: e.target.value }))} />
                          : o.name}
                      </td>
                      {!mlsId && (
                        <td data-label="MLS">
                          {editing
                            ? <select className="sa-inline" value={editDraft.mlsId || ''} onChange={e => setEditDraft(d => ({ ...d, mlsId: e.target.value }))}>
                                <option value="">— Select —</option>
                                {mlsCatalog.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                              </select>
                            : mlsName(o.mlsId)}
                        </td>
                      )}
                      <td data-label="Seats"><span className={(eff > 0 && used >= eff) ? 'sa-seats-full' : ''}>{used}</span><span className="sa-seats-cap"> / {eff}</span></td>
                      <td data-label="Trial">
                        {editing
                          ? <span className="sa-trial-edit">
                              <input className="sa-inline sa-inline-num" type="number" min="0" value={editDraft.trialSeats} title="Trial seats" onChange={e => setEditDraft(d => ({ ...d, trialSeats: e.target.value }))} />
                              <input className="sa-inline sa-inline-num" type="number" min="0" value={editDraft.trialDays} title="Trial days" onChange={e => setEditDraft(d => ({ ...d, trialDays: e.target.value }))} />
                            </span>
                          : (o.trialDays ? `${o.trialSeats || 0} seats / ${o.trialDays} d` : '—')}
                      </td>
                      <td data-label="Seats">
                        {editing
                          ? <input className="sa-inline sa-inline-num" type="number" min="0" value={editDraft.seats} title="Purchased seats — how many agents this org may have" onChange={e => setEditDraft(d => ({ ...d, seats: e.target.value }))} />
                          : (Number(o.seats) || 0)}
                      </td>
                      <td className="sa-sub" data-label="Subscription">
                        <input type="date" className="sa-inline sa-date-input"
                          title="Paid-subscription end date — set after the org pays; governs when all its agents expire. Blank = not subscribed (runs on the trial → seats lifecycle)."
                          value={o.expiresAt ? String(o.expiresAt).slice(0, 10) : ''}
                          onChange={e => onSetOrgExpiry && onSetOrgExpiry(o.id, e.target.value ? new Date(e.target.value + 'T12:00:00').toISOString() : null)} />
                        {o.expiresAt
                          ? (subscribed
                              ? <span className="sa-status on">until {fmtDate(o.expiresAt)}</span>
                              : <span className="sa-status off">lapsed {fmtDate(o.expiresAt)}</span>)
                          : <span className="sa-muted">not subscribed</span>}
                      </td>
                      <td data-label="Status">
                        {o.disabled
                          ? <span className="sa-status off">Suspended</span>
                          : expired
                            ? <span className="sa-status off">Expired</span>
                            : subscribed
                              ? <span className="sa-status on">Paid</span>
                              : inTrial
                                ? <span className={`sa-status ${daysLeft <= 7 ? 'trial-soon' : 'trial'}`}>Trial · {daysLeft}d</span>
                                : <span className="sa-status on">Paid</span>}
                      </td>
                      <td className="sa-admins" data-label="Admins">{admins.map(a => (a.email && a.email.toLowerCase() !== a.username.toLowerCase()) ? `${a.username} (${a.email})` : (a.email || a.username)).join(', ') || '—'}</td>
                      <td className="sa-actions">
                        {editing ? (
                          <>
                            <button className="sa-save" onClick={saveEdit}>Save</button>
                            <button className="contacts-import-cancel-btn" onClick={() => setEditId(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className="sa-btn" onClick={() => startEdit(o)}>Edit</button>
                            <button className="sa-btn" onClick={() => setManageId(manageId === o.id ? null : o.id)}>Admins</button>
                            <button className="sa-btn" onClick={() => onSetOrgDisabled(o.id, !o.disabled)}>{o.disabled ? 'Enable' : 'Suspend'}</button>
                            {onManage && isDevMode() && <button className="sa-btn dev-tool" title="Developer-mode tool — drills into this organization's interface" onClick={() => onManage(o.id)}>Manage</button>}
                            <button className="sa-btn sa-btn-del" onClick={() => startDelete(o)}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {manageId === o.id && (
                      <tr className="sa-manage-row">
                        <td colSpan={mlsId ? 8 : 9}>
                          <div className="sa-manage">
                            <div className="sa-manage-title">Admins for {o.name}</div>
                            <ul className="sa-admin-list">
                              {admins.length ? admins.map(a => (
                                <li key={a.id} className="sa-admin-li">
                                  <div className="sa-admin-row">
                                    <span className="sa-admin-name">{a.username}{a.disabled ? ' (disabled)' : ''}{a.mustChangePassword ? ' · must reset' : ''}</span>
                                    <span className="sa-admin-email">{a.email || '— no email —'}</span>
                                    <button className="sa-link" onClick={() => doReset(a.id, a.username, a.email)}>Send reset link</button>
                                  </div>
                                </li>
                              )) : <li className="sa-muted">No admins yet.</li>}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {systemOrgs.length > 0 && (
        <>
          <div className="sa-header sa-sysorg-header" style={{ marginTop: 8 }}>
            <div>
              <h2 className="sa-sysorg-title"><Icon name="system-orgs" size={18} style={{verticalAlign:'-3px'}} /> System Organizations</h2>
              <p>Agents who signed up directly with iBetterAgent for this MLS land here, each with their own expiration; this System Org never expires. Manage its reminder templates and members from here.</p>
            </div>
          </div>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr><th>System Org</th>{!mlsId && <th>MLS</th>}<th><span className="sa-label-full">Direct members</span><span className="sa-label-short">Members</span></th><th>Status</th><th className="sa-th-actions">Actions</th></tr>
              </thead>
              <tbody>
                {systemOrgs.map(o => {
                  const members = membersOf(o.id);
                  const active = members.filter(a => !a.disabled).length;
                  return (
                    <tr key={o.id}>
                      <td className="sa-org-name" data-label="System Org"><span className="sa-label-full">{o.name}</span><span className="sa-label-short">iBetterAgent</span></td>
                      {!mlsId && <td data-label="MLS">{mlsName(o.mlsId)}</td>}
                      <td data-label="Members">{active}{members.length !== active ? ` active / ${members.length} total` : ''}</td>
                      <td data-label="Status"><span className="sa-status on"><span className="sa-label-full">System · never expires</span><span className="sa-label-short">System</span></span></td>
                      <td className="sa-actions">
                        <button className="sa-btn" onClick={() => onManage && onManage(o.id)}>Manage</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default SystemAdmin;
